import { recordPremiumUnlock } from './premiumClickEvents';
import { premiumOffer } from '../pages/PremiumPage/offer';
import { trackUmamiEvent } from './umami';
import { currentPremiumAttribution, rememberPremiumCheckout, rememberPremiumSource } from './premiumAttribution';

import { analyticsAccountType } from './analyticsAccountType';
export { setPremiumAccountTypeSource } from './analyticsAccountType';

export interface PremiumClickContext {
  placement: string;
  feature?: string;
  context?: string;
  action?: string;
  theme?: string;
  purchaseType?: 'upgrade' | 'initial_purchase';
  currentTier?: string;
  targetTier?: string;
}

/** Group routes without transmitting game keys, account IDs, or query strings. */
export function premiumPageContext(path: string): string {
  const publicPages: Record<string, string> = {
    '/secret-santa-for-friends': 'landing_friends',
    '/secret-santa-for-coworkers': 'landing_coworkers',
    '/secret-santa-for-family': 'landing_family',
    '/secret-santa-for-classroom': 'landing_classroom',
    '/white-elephant': 'landing_white_elephant',
    '/faq': 'faq', '/randomizer': 'quick_draw',
    '/login': 'login', '/register': 'register', '/recover': 'recovery',
    '/account/profiles': 'profiles', '/account/boxes': 'my_games',
    '/account/notifications': 'notifications', '/notifications': 'notifications',
  };
  if (publicPages[path]) return publicPages[path];
  if ((path === '/premium/buy' || path.startsWith('/premium/buy/'))) return 'premium_buy';
  if (path === '/premium') return 'premium_page';
  if (path.startsWith('/box/new')) return 'new_game';
  if (path.startsWith('/account')) return 'account';
  if (path.includes('/settings')) return 'game_settings';
  if (path.includes('/manage') || path.includes('/admin') || path.includes('/organizer')) return 'game_management';
  if (path.startsWith('/box/')) return 'game';
  if (path === '/') return 'home';
  if (path === '/terms' || path === '/policy' || path === '/privacy_policy') return 'legal';
  return 'other';
}

/** Only game-flow upsells qualify, regardless of inherited checkout attribution. */
export function isGameUpsellContext(path: string, context?: string): boolean {
  return context === 'copy_game' || /^\/box\/[^/]+(?:\/|$)/.test(path) &&
    !['profile', 'account', 'premium_page', 'premium_buy'].includes(context ?? '');
}

export function trackPremium(
  event: 'premium_offer_upsell_viewed_v2' | 'premium_offer_viewed_v2' | 'premium_unlock_clicked_v2' | 'premium_navigation_clicked_v2' | 'premium_feature_gate_clicked_v2' | 'premium_learn_more_click_v2' | 'premium_buy_click_v2' | 'premium_email_request_sent_v2' | 'premium_email_request_cancelled_v2' | 'premium_complete_purchase_v2' | 'premium_failed_purchase_v2',
  details: PremiumClickContext,
  quotedOffer?: ReturnType<typeof premiumOffer>,
): void {
  // Analytics must never interrupt navigation or a successful payment request.
  try {
    const offer = quotedOffer ?? premiumOffer();
    if (event === 'premium_offer_upsell_viewed_v2' &&
      (!offer.ready || !isGameUpsellContext(window.location.pathname, details.context))) return;
    if (event === 'premium_unlock_clicked_v2' && details.action === 'open_buy_page') {
      recordPremiumUnlock(premiumPageContext(window.location.pathname), details, offer);
    }
    const exposure = event === 'premium_offer_viewed_v2' || event === 'premium_offer_upsell_viewed_v2';
    const paymentReturn = event === 'premium_complete_purchase_v2' || event === 'premium_failed_purchase_v2';
    const source = {
      feature: details.feature ?? 'all',
      context: details.context ?? premiumPageContext(window.location.pathname),
      placement: details.placement,
    };
    const discovery = exposure || [ 'premium_navigation_clicked_v2', 'premium_feature_gate_clicked_v2', 'premium_learn_more_click_v2'].includes(event);
    const downstream = event !== 'premium_unlock_clicked_v2' && !discovery;
    const existing = currentPremiumAttribution(paymentReturn);
    const attribution = paymentReturn || discovery ? existing : rememberPremiumSource(downstream && existing ? { ...source, feature: 'all' } : source);
    trackUmamiEvent(event, {
      page: premiumPageContext(window.location.pathname),
      placement: details.placement,
      account_type: analyticsAccountType(),
      feature: source.feature === 'all' && !exposure ? attribution?.latest.feature ?? 'all' : source.feature,
      context: downstream && source.context === 'premium_buy' ? attribution?.latest.context ?? source.context : source.context,
      ...(attribution ? {
        first_feature: attribution.first.feature,
        first_context: attribution.first.context,
        first_placement: attribution.first.placement,
        last_feature: attribution.latest.feature,
        last_context: attribution.latest.context,
        last_placement: attribution.latest.placement,
      } : {}),
      action: details.action ?? 'open_comparison',
      ...(details.theme ? { theme: details.theme } : {}),
      ...(details.purchaseType ? { purchase_type: details.purchaseType, current_tier: details.currentTier ?? 'free', target_tier: details.targetTier } : {}),
      site: offer.site,
      price: offer.ready ? (offer.checkoutAmountMinor !== undefined ? offer.checkoutAmountMinor / 100 : offer.amount) : null,
      ...(offer.checkoutAmountMinor !== undefined ? { test_payment: true, displayed_price: offer.amount } : {}),
      currency: offer.currency,
      experiment: offer.experiment ?? 'unknown',
      ...(exposure ? { visible_ms: 1000 } : {}),
      ...(event === 'premium_offer_upsell_viewed_v2' ? { visible_percent: 100 } : {}),
      variant: offer.variant ?? 'unknown',
      device: window.matchMedia('(max-width: 48rem)').matches ? 'mobile' : 'desktop',
    });
  } catch { /* Tracking is best effort. */ }
}

/** Only known UI categories can pass through buy-page URL attribution. */
export function premiumBuyAttribution(search: string) {
  const query = new URLSearchParams(search);
  const feature = query.get('feature') ?? '';
  const context = query.get('context') ?? '';
  return {
    feature: ['account', 'themes', 'organizers', 'questions', 'history', 'draw', 'address'].includes(feature) ? feature : 'all',
    context: ['creation', 'settings', 'profile', 'copy_game'].includes(context) ? context : 'premium_buy',
  };
}

/** Track bank-return landings once per history entry, including across reloads. */
export function trackPremiumPurchaseResult(result: string | null, quotedOffer?: ReturnType<typeof premiumOffer>): void {
  if (result !== 'success' && result !== 'failed') return;
  try {
    if (window.history.state?.ssPremiumPurchaseResult === result) return;
    // Preserve React Router's key/index/user state. A new return navigation gets a new entry.
    window.history.replaceState({ ...window.history.state, ssPremiumPurchaseResult: result }, '');
  } catch { /* Storage restrictions must not interrupt payment confirmation. */ }
  trackPremium(result === 'success' ? 'premium_complete_purchase_v2' : 'premium_failed_purchase_v2', {
    ...premiumBuyAttribution(window.location.search),
    placement: 'payment_return',
    action: result === 'success' ? 'payment_success' : 'payment_failed',
  }, quotedOffer);
}

/** Preserve the origin before the bank tab is opened, including when the API is slow. */
export function capturePremiumCheckoutAttribution(): void {
  rememberPremiumCheckout({ ...premiumBuyAttribution(window.location.search), placement: 'payment_button' });
}
